/**
 * AuthSendCodeStep — the step before {@link AuthOtpStep}: ask where the code
 * should go, then send it.
 *
 * verify-email and verify-phone both start here when the account has no
 * contact staged yet (a Google/Apple user linking their first phone, or an
 * account linking its first email). The destination field is the only real
 * difference, so it comes in as children.
 */
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { AuthGradientButton } from './AuthGradientButton'
import { SPACING } from '../../lib/brand'
import { DURATION, STAGGER } from '../../lib/theme/tokens'

interface AuthSendCodeStepProps {
  /** The destination field — an EmailInput or a PhoneInput. */
  children: ReactNode
  buttonTitle: string
  onSend: () => void
  /** True while the send request is in flight. */
  loading?: boolean
  /**
   * Inert until the destination is one a code can actually reach.
   *
   * Same barrier as the phone-entry screen, in the shared step, so linking a
   * phone after signup cannot spend a credit that entering one at signup would
   * have refused.
   */
  disabled?: boolean
  /** Screen-scoped id for the send control, e.g. `verify-phone.send-button`. */
  testID: string
}

export function AuthSendCodeStep({
  children,
  buttonTitle,
  onSend,
  loading,
  disabled,
  testID,
}: AuthSendCodeStepProps) {
  const reducedMotion = useReducedMotion()

  return (
    <Animated.View
      collapsable={false}
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)}
    >
      {children}
      <View style={{ marginTop: SPACING.sm }}>
        <AuthGradientButton
          title={buttonTitle}
          onPress={onSend}
          loading={loading}
          disabled={disabled}
          testID={testID}
        />
      </View>
    </Animated.View>
  )
}
